import Box from '@mui/material/Box'
import { grey } from '@mui/material/colors'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import SvgIcon from '@mui/material/SvgIcon'
import { Photograph } from '@styled-icons/heroicons-outline'
import React, { useCallback, useState } from 'react'

import WorkOrdersPhotos from 'components/drawer/work-orders-photos/WorkOrdersPhotos'
import StyledBadge from 'components/notes/common/StyledBadge'
import ExceptionImage from 'components/work-order/details/exceptions/ExceptionImage'
import { Z_INDEX } from 'theme/zIndex'

import type { ExceptionBadgeException } from 'components/customer-details/content/work-orders/actions/exception/ExceptionBadge'

type Props = {
  exceptions?: ExceptionBadgeException[]
}

const PhotosBadge: React.FC<Props> = ({ exceptions = [] }) => {
  const [viewPopoverAnchorEl, setViewPopoverAnchorEl] = useState<HTMLElement | null>(null)
  const [isOpenPhotosDialog, setIsOpenPhotosDialog] = useState(false)
  const exceptionsWithAttachment = exceptions.filter(exception => exception.fileLocation)
  const exceptionsWithAttachmentCount = exceptionsWithAttachment.length
  const hasExceptionsWithAttachment = exceptionsWithAttachmentCount > 0

  const handleViewPopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setViewPopoverAnchorEl(event.currentTarget)
  }

  const handleViewPopoverClose = () => setViewPopoverAnchorEl(null)

  const handleOpenPhotosDialog = () => setIsOpenPhotosDialog(true)

  const handleClosePhotosDialog = useCallback(() => setIsOpenPhotosDialog(false), [])

  return (
    <>
      <IconButton
        size="small"
        disabled={!hasExceptionsWithAttachment}
        {...(hasExceptionsWithAttachment && {
          onMouseEnter: handleViewPopoverOpen,
          onMouseLeave: handleViewPopoverClose,
          onClick: handleOpenPhotosDialog,
        })}
      >
        <StyledBadge badgeContent={exceptionsWithAttachmentCount}>
          <SvgIcon>
            <Photograph />
          </SvgIcon>
        </StyledBadge>
      </IconButton>
      <Popover
        sx={{ pointerEvents: 'none', zIndex: Z_INDEX.BADGE_POPOVER }}
        open={Boolean(viewPopoverAnchorEl)}
        anchorEl={viewPopoverAnchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={handleViewPopoverClose}
        disableRestoreFocus
        slotProps={{ paper: { sx: { background: grey[800] } } }}
      >
        <Stack direction="row" spacing={1} sx={{ p: 1.5 }} flexWrap="wrap">
          {exceptionsWithAttachment.map(exception => (
            <Box key={exception.fileLocation} flex={1} minWidth={150}>
              <ExceptionImage
                exception={{
                  ...exception,
                  images: [{ original: { fileLocation: exception.fileLocation ?? '' } }],
                }}
                ImageSx={{ maxHeight: 150, overflow: 'hidden' }}
                PageProps={{ height: 150, width: 150, scale: 1 }}
                size="small"
                withSwiperSlide={false}
              />
            </Box>
          ))}
        </Stack>
      </Popover>
      <WorkOrdersPhotos exceptions={exceptionsWithAttachment} isOpenDrawer={isOpenPhotosDialog} onClose={handleClosePhotosDialog} />
    </>
  )
}

export default PhotosBadge
